const toNormalizedToneMap = (hue) => {
    if (!hue) return null;

    const candidates = Array.isArray(hue.dominantHues) && hue.dominantHues.length > 0
        ? hue.dominantHues
        : (hue.tone && hue.tone !== 'ACHROMATIC' ? [{ tone: hue.tone, ratio: 1 }] : []);

    if (candidates.length === 0) return null;

    const total = candidates.reduce((sum, item) => {
        const ratio = Number(item?.ratio);
        return sum + (Number.isFinite(ratio) && ratio > 0 ? ratio : 0);
    }, 0);

    const fallbackRatio = 1 / candidates.length;
    const toneMap = new Map();

    candidates.forEach((item) => {
        const tone = item?.tone;
        if (!tone) return;
        const rawRatio = Number(item?.ratio);
        const ratio = total > 0
            ? ((Number.isFinite(rawRatio) && rawRatio > 0 ? rawRatio : 0) / total)
            : fallbackRatio;
        toneMap.set(tone, ratio);
    });

    return toneMap.size > 0 ? toneMap : null;
};

const isAchromaticHue = (hue) => {
    return Boolean(hue?.isAchromatic) || hue?.tone === 'ACHROMATIC';
};

const calculateOverlapRatio = (globalHue, partitionHue) => {
    const globalMap = toNormalizedToneMap(globalHue);
    const partitionMap = toNormalizedToneMap(partitionHue);
    if (!globalMap || !partitionMap) return 0;

    let overlap = 0;
    globalMap.forEach((globalRatio, tone) => {
        const partitionRatio = partitionMap.get(tone);
        if (partitionRatio != null) {
            overlap += Math.min(globalRatio, partitionRatio);
        }
    });
    return overlap;
};

export const HSL_HUE_RULES = {
    OVERALL_HUE_CONSISTENCY: {
        condition: (globalHue, partitionsData) => {
            if (!globalHue || isAchromaticHue(globalHue)) return false;

            const partitions = [
                partitionsData?.blacks,
                partitionsData?.shadows,
                partitionsData?.midtones,
                partitionsData?.highlights,
                partitionsData?.whites
            ].filter(Boolean);

            let consistentCount = 0;
            partitions.forEach((partition) => {
                const partitionHue = partition?.hue;
                if (!partitionHue || isAchromaticHue(partitionHue)) return;

                const overlapRatio = calculateOverlapRatio(globalHue, partitionHue);
                if (overlapRatio >= 0.5) {
                    consistentCount += 1;
                }
            });

            return consistentCount >= 3;
        }
    },
    OVERALL_HUE_VARIATIONS: {
        condition: (globalHue, partitionsData) => {
            if (!globalHue || isAchromaticHue(globalHue)) return null;

            const partitions = [
                { name: 'blacks', data: partitionsData?.blacks },
                { name: 'shadows', data: partitionsData?.shadows },
                { name: 'midtones', data: partitionsData?.midtones },
                { name: 'highlights', data: partitionsData?.highlights },
                { name: 'whites', data: partitionsData?.whites }
            ].filter(partition => partition.data);

            const variations = partitions
                .filter(partition => {
                    const partitionHue = partition.data?.hue;
                    if (!partitionHue || isAchromaticHue(partitionHue)) return false;
                    return calculateOverlapRatio(globalHue, partitionHue) < 0.2;
                })
                .map(partition => ({
                    name: partition.name,
                    dominantHues: partition.data.hue.dominantHues || [],
                    temperature: partition.data.hue.temperature,
                    overlapRatio: calculateOverlapRatio(globalHue, partition.data.hue)
                }));

            return variations.length > 0 ? variations : null;
        }
    },
    SHADOW_HIGHLIGHT_HUE_CONTRAST: {
        condition: (_globalHue, partitionsData) => {
            const shadowTemp = partitionsData?.shadows?.hue?.temperature;
            const highlightTemp = partitionsData?.highlights?.hue?.temperature;

            if (!shadowTemp || !highlightTemp) return false;

            return (
                (shadowTemp === 'WARM' && highlightTemp === 'COOL') ||
                (shadowTemp === 'COOL' && highlightTemp === 'WARM')
            );
        }
    }
};
